import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { useWorkflowConnectors } from '@/hooks/useWorkflowConnectors';
import type { NodeConnectorInfo } from '@/types/connections';

type ConnectorContextValue = {
  getConnectorInfo: (connectionName: string) => NodeConnectorInfo | undefined;
  isLoading: boolean;
};

type ConnectorStoreState = ConnectorContextValue & {
  connectorMap: ReadonlyMap<string, NodeConnectorInfo>;
  setSnapshot: (
    connectorMap: ReadonlyMap<string, NodeConnectorInfo>,
    isLoading: boolean,
  ) => void;
};

type ConnectorStore = StoreApi<ConnectorStoreState>;

const ConnectorContext = createContext<ConnectorStore | undefined>(undefined);

const EMPTY_CONNECTOR_MAP = new Map<string, NodeConnectorInfo>();
const getMissingConnectorInfo = (): undefined => undefined;

const connectorInfoEqual = (
  left: NodeConnectorInfo,
  right: NodeConnectorInfo,
): boolean =>
  left.displayName === right.displayName &&
  left.iconUri === right.iconUri &&
  left.brandColor === right.brandColor &&
  left.connectorType === right.connectorType;

const reconcileConnectorMap = (
  previous: ReadonlyMap<string, NodeConnectorInfo>,
  next: ReadonlyMap<string, NodeConnectorInfo>,
): ReadonlyMap<string, NodeConnectorInfo> => {
  if (previous === next) return previous;

  let changed = previous.size !== next.size;
  const reconciled = new Map<string, NodeConnectorInfo>();
  for (const [key, nextInfo] of next) {
    const previousInfo = previous.get(key);
    const info =
      previousInfo && connectorInfoEqual(previousInfo, nextInfo)
        ? previousInfo
        : nextInfo;
    if (info !== previousInfo) changed = true;
    reconciled.set(key, info);
  }

  return changed ? reconciled : previous;
};

const createConnectorStore = (
  initialMap: ReadonlyMap<string, NodeConnectorInfo> = EMPTY_CONNECTOR_MAP,
  initialIsLoading = false,
): ConnectorStore =>
  createStore<ConnectorStoreState>((set) => ({
    connectorMap: initialMap,
    getConnectorInfo: (connectionName) => initialMap.get(connectionName),
    isLoading: initialIsLoading,
    setSnapshot: (nextMap, isLoading) => {
      set((state) => {
        const connectorMap = reconcileConnectorMap(state.connectorMap, nextMap);
        const mapChanged = connectorMap !== state.connectorMap;
        if (!mapChanged && isLoading === state.isLoading) return state;

        return {
          connectorMap,
          getConnectorInfo: mapChanged
            ? (connectionName) => connectorMap.get(connectionName)
            : state.getConnectorInfo,
          isLoading,
        };
      });
    },
  }));

const fallbackConnectorStore = createConnectorStore();
const selectGetConnectorInfo = (state: ConnectorStoreState) => state.getConnectorInfo;
const selectIsLoading = (state: ConnectorStoreState) => state.isLoading;
const createSelectConnectorInfo =
  (connectionName: string | undefined) =>
  (state: ConnectorStoreState): NodeConnectorInfo | undefined =>
    connectionName ? state.connectorMap.get(connectionName) : undefined;

type ConnectorProviderProps = {
  children: ReactNode;
};

/**
 * Provider that makes connector info available to all workflow nodes
 */
export const ConnectorProvider = ({ children }: ConnectorProviderProps) => {
  const { connectorMap, isLoading } = useWorkflowConnectors();
  const storeRef = useRef<ConnectorStore | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createConnectorStore(connectorMap, isLoading);
  }
  const store = storeRef.current;

  useLayoutEffect(() => {
    store.getState().setSnapshot(connectorMap, isLoading);
  }, [connectorMap, isLoading, store]);

  return <ConnectorContext.Provider value={store}>{children}</ConnectorContext.Provider>;
};

/**
 * Hook to get connector info for a specific connection
 */
export const useConnectorInfo = (connectionName: string | undefined): NodeConnectorInfo | undefined => {
  const store = useContext(ConnectorContext) ?? fallbackConnectorStore;
  const selector = useMemo(
    () => createSelectConnectorInfo(connectionName),
    [connectionName],
  );
  return useStore(store, selector);
};

/**
 * Hook to access the full connector context
 */
export const useConnectorContext = (): ConnectorContextValue => {
  const store = useContext(ConnectorContext);

  if (store === undefined) {
    throw new Error('useConnectorContext must be used within a ConnectorProvider');
  }

  const getConnectorInfo = useStore(store, selectGetConnectorInfo);
  const isLoading = useStore(store, selectIsLoading);
  return useMemo(
    () => ({ getConnectorInfo, isLoading }),
    [getConnectorInfo, isLoading],
  );
};

export const useConnectorLoading = (): boolean => {
  const store = useContext(ConnectorContext) ?? fallbackConnectorStore;
  return useStore(store, selectIsLoading);
};

/**
 * Hook to get the getConnectorInfo function safely (returns no-op outside provider)
 */
export const useGetConnectorInfo = (): ((connectionName: string) => NodeConnectorInfo | undefined) => {
  const store = useContext(ConnectorContext) ?? fallbackConnectorStore;
  return useStore(store, selectGetConnectorInfo) ?? getMissingConnectorInfo;
};
